import { useState } from "react";
import { Facebook, Instagram, Youtube, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useBooking } from "@/components/site/BookingProvider";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const { openBooking } = useBooking();

  const onSubmit = (e) => {
    e.preventDefault();
    if (!email || !/.+@.+\..+/.test(email)) {
      toast.error("Please enter a valid email.");
      return;
    }
    // Mock submission
    console.log("[Newsletter mock submit]", email);
    setSubmitted(true);
    toast.success("Welcome to the Longevity Lounge list. We'll be in touch.");
    setEmail("");
  };

  return (
    <footer
      data-testid="site-footer"
      className="relative bg-stone text-bone mll-grain"
    >
      {/* CTA banner */}
      <div className="border-b border-bone/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-20 sm:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mll-reveal">
            <div className="lg:col-span-7">
              <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-clay-soft">
                Join the Longevity Lounge
              </p>
              <h2 className="mt-4 font-serif font-medium text-4xl sm:text-5xl lg:text-[64px] tracking-tight leading-[1.05]">
                Be first to hear of new modalities,{" "}
                <em className="italic font-normal text-clay-soft">
                  member events
                </em>
                , and seasonal protocols.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <form
                onSubmit={onSubmit}
                data-testid="newsletter-form"
                className="flex items-stretch border-b border-bone/30 focus-within:border-clay-soft transition-colors"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="newsletter-input"
                  className="flex-1 bg-transparent py-4 text-base text-bone placeholder:text-bone/40 outline-none"
                />
                <button
                  type="submit"
                  data-testid="newsletter-submit"
                  className="inline-flex items-center gap-2 px-4 text-[12px] uppercase tracking-[0.22em] font-semibold text-bone hover:text-clay-soft transition-colors"
                >
                  {submitted ? "Subscribed" : "Subscribe"}
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </form>
              <p className="mt-3 text-xs text-bone/50">
                No spam. Unsubscribe in one click. We will not share your
                address.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom */}
      <div className="max-w-7xl mx-auto px-6 sm:px-8 py-14 sm:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <a href="#top" className="inline-flex items-center gap-3">
              <img
                src="https://images.leadconnectorhq.com/image/f_webp/q_80/r_320/u_https://assets.cdn.filesafe.space/UL9GAEU0ONW7drLQGkjG/media/685dbe808f84bd026b3f6756.png"
                alt="Maui Longevity Lounge"
                className="h-12 w-auto"
              />
            </a>
            <p className="mt-6 text-sm text-bone/65 leading-relaxed max-w-md">
              Cultivating holistic health through clinical-grade longevity
              technology and the natural rhythm of Haiku, Maui.
            </p>
            <div className="mt-8 flex items-center gap-3">
              <Social
                href="https://facebook.com/"
                label="Facebook"
                icon={<Facebook className="w-4 h-4" />}
              />
              <Social
                href="https://instagram.com/"
                label="Instagram"
                icon={<Instagram className="w-4 h-4" />}
              />
              <Social
                href="https://youtube.com/"
                label="YouTube"
                icon={<Youtube className="w-4 h-4" />}
              />
            </div>
          </div>

          <FooterCol
            heading="Lounge"
            links={[
              { label: "Technology", href: "#technology" },
              { label: "Membership", href: "#membership" },
              { label: "Stories", href: "#stories" },
              { label: "FAQ", href: "#faq" },
            ]}
          />
          <FooterCol
            heading="Visit"
            links={[
              { label: "810 Kokomo Rd, Suite 120", href: "#visit" },
              { label: "Haiku, HI 96708", href: "#visit" },
              { label: "Mon – Fri · 8am – 5pm", href: "#visit" },
            ]}
          />
          <FooterCol
            heading="Reach us"
            links={[
              { label: "808.500.8857", href: "tel:8085008857" },
              {
                label: "staff@mauilongevitylounge.com",
                href: "mailto:staff@mauilongevitylounge.com",
              },
              {
                label: "Book a Session",
                onClick: openBooking,
              },
            ]}
          />
        </div>

        <div className="mt-16 pt-8 border-t border-bone/10 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
          <p className="text-xs text-bone/50">
            © {new Date().getFullYear()} Maui Longevity Lounge · A FuzionFit
            company. All rights reserved.
          </p>
          <p className="text-xs text-bone/50 italic font-serif">
            Designed with quiet intention on Maui.
          </p>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({ heading, links }) {
  return (
    <div className="lg:col-span-2">
      <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-bone/55">
        {heading}
      </p>
      <ul className="mt-5 space-y-3">
        {links.map((l, i) => (
          <li key={i}>
            {l.onClick ? (
              <button
                type="button"
                onClick={l.onClick}
                className="text-sm text-bone/85 hover:text-clay-soft transition-colors text-left"
              >
                {l.label}
              </button>
            ) : (
              <a
                href={l.href}
                target={l.external ? "_blank" : undefined}
                rel={l.external ? "noopener noreferrer" : undefined}
                className="text-sm text-bone/85 hover:text-clay-soft transition-colors"
              >
                {l.label}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Social({ href, label, icon }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={`social-${label.toLowerCase()}`}
      className="inline-flex items-center justify-center w-10 h-10 border border-bone/25 text-bone/85 hover:bg-bone hover:text-stone transition-colors"
    >
      {icon}
    </a>
  );
}
